import { api } from './api';

/** Convierte la clave VAPID (base64url) a Uint8Array para PushManager.subscribe. */
function urlBase64ToUint8Array(base64: string): Uint8Array {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const b64 = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(b64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

export function pushSupported(): boolean {
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

/** ¿La app está corriendo como PWA instalada (standalone)? En iOS el push SOLO funciona así. */
export function isStandalone(): boolean {
  return window.matchMedia?.('(display-mode: standalone)').matches
    || (navigator as unknown as { standalone?: boolean }).standalone === true;
}

/** ¿Es un dispositivo iOS/iPadOS? (Safari iOS exige "Agregar a inicio" para el push.) */
export function isIOS(): boolean {
  const ua = navigator.userAgent || '';
  return /iPad|iPhone|iPod/.test(ua) || (/Macintosh/.test(ua) && 'ontouchend' in document);
}

/** Estado actual del permiso de notificaciones del navegador. */
export type PushPermission = 'unsupported' | 'default' | 'granted' | 'denied';
export function pushPermission(): PushPermission {
  if (!pushSupported()) return 'unsupported';
  return Notification.permission as PushPermission;
}

/** Endpoint de ESTE navegador (null si no hay suscripción local). */
export async function currentEndpoint(): Promise<string | null> {
  try {
    if (!pushSupported()) return null;
    const reg = await navigator.serviceWorker.getRegistration();
    const sub = await reg?.pushManager.getSubscription();
    return sub?.endpoint ?? null;
  } catch { return null; }
}

/** Resultado real del test (el server ya no esconde los fallos de envío).
 *  `thisDevice` = si ESTE navegador está registrado en el servidor. */
export type TestPushResult = {
  subscriptions: number; thisDevice: boolean | null; vapid: boolean;
  attempted: number; sent: number; failed: Array<{ status: number | null }>;
};
/** Manda un push de PRUEBA a ESTE equipo (no a todos los del usuario). */
export async function sendTestPush(): Promise<TestPushResult> {
  const endpoint = await currentEndpoint();
  const r = await api.post<Partial<TestPushResult>>('/api/push/test', endpoint ? { endpoint } : {});
  return {
    subscriptions: r.subscriptions ?? 0, thisDevice: r.thisDevice ?? null, vapid: r.vapid !== false,
    attempted: r.attempted ?? 0, sent: r.sent ?? 0, failed: r.failed ?? [],
  };
}

/** Versión del sw.js que espera esta build. Si el worker ACTIVO reporta menos (o no contesta),
 *  el navegador está corriendo un worker viejo y el diagnóstico de entrega no es concluyente. */
export const SW_VERSION_ESPERADA = 4;

/** Le pregunta al service worker ACTIVO qué versión es. null = no contestó (worker viejo). */
export async function activeSwVersion(): Promise<number | null> {
  try {
    if (!('serviceWorker' in navigator)) return null;
    const reg = await navigator.serviceWorker.getRegistration();
    const sw = reg?.active;
    if (!sw) return null;
    return await new Promise<number | null>((resolve) => {
      const ch = new MessageChannel();
      const timer = setTimeout(() => resolve(null), 1500); // los viejos no responden nada
      ch.port1.onmessage = (e) => {
        clearTimeout(timer);
        resolve(typeof e.data?.version === 'number' ? e.data.version : null);
      };
      sw.postMessage({ type: 'version' }, [ch.port2]);
    });
  } catch { return null; }
}

/** Entrega real de ESTE navegador: qué mandó el server y qué CONFIRMÓ el navegador. */
export type PushDelivery = {
  sentAt: string | null; ackAt: string | null; seenAt: string | null; lastError: string | null;
};

/** Estado en el SERVIDOR: cuántos dispositivos tiene el usuario y si ESTE está entre ellos. */
export async function pushServerStatus(): Promise<{
  subscriptions: number; thisDevice: boolean | null; delivery: PushDelivery | null;
}> {
  try {
    const endpoint = await currentEndpoint();
    const qs = endpoint ? `?endpoint=${encodeURIComponent(endpoint)}` : '';
    const r = await api.get<{ subscriptions: number; thisDevice: boolean | null; delivery: PushDelivery | null }>(`/api/push/status${qs}`);
    return { subscriptions: r.subscriptions ?? 0, thisDevice: r.thisDevice ?? null, delivery: r.delivery ?? null };
  } catch { return { subscriptions: 0, thisDevice: null, delivery: null }; }
}

/**
 * Tras el test, espera a que el SERVICE WORKER acuse recibo (hasta ~10s).
 * - `arrived` → el push cruzó hasta el navegador.
 * - `swError` → llegó pero showNotification FALLÓ (el "sw: …" de la fila): el problema es la
 *   capa de pantalla, no el transporte — cambia por completo el consejo al usuario.
 */
export async function waitForDeliveryAck(desde: number): Promise<{ arrived: boolean; swError: string | null }> {
  for (let i = 0; i < 10; i++) {
    await new Promise((r) => { setTimeout(r, 1000); });
    const { delivery } = await pushServerStatus();
    if (delivery?.ackAt && new Date(delivery.ackAt).getTime() >= desde) {
      const err = delivery.lastError && delivery.lastError.startsWith('sw:') ? delivery.lastError : null;
      return { arrived: true, swError: err };
    }
  }
  return { arrived: false, swError: null };
}

/** ¿La suscripción existente fue creada con ESTA clave VAPID? Si el navegador guarda una sub
 *  de una clave vieja, el server firma con la nueva y el push service rechaza TODO (403) sin
 *  que el usuario vea nada. Hay que darla de baja y re-suscribir con la clave vigente. */
function subMatchesKey(sub: PushSubscription, key: Uint8Array): boolean {
  const raw = (sub.options as { applicationServerKey?: ArrayBuffer | null }).applicationServerKey;
  if (!raw) return true; // navegador viejo que no expone la clave: no podemos comparar
  const cur = new Uint8Array(raw);
  if (cur.length !== key.length) return false;
  for (let i = 0; i < cur.length; i++) if (cur[i] !== key[i]) return false;
  return true;
}

/**
 * Registra el SW, pide permiso y suscribe al usuario. Devuelve true si quedó activo.
 *
 * `force` (botón Re-sincronizar): DA DE BAJA la suscripción actual y crea una nueva. Es la
 * diferencia entre arreglar y no arreglar el caso zombi: una suscripción puede quedar muerta
 * (el push service acepta el envío con 201 pero el navegador ya no la escucha) y
 * `getSubscription()` la sigue devolviendo tal cual. Sin el force, re-sincronizar volvía a
 * guardar EXACTAMENTE la misma suscripción rota y el usuario podía darle mil veces sin efecto.
 */
export async function enablePush(force = false): Promise<boolean> {
  if (!pushSupported()) throw new Error('push_unsupported');
  const perm = await Notification.requestPermission();
  if (perm !== 'granted') throw new Error('permission_denied');
  const reg = await navigator.serviceWorker.register('/sw.js');
  // Fuerza el chequeo de version: si hay un sw.js nuevo en el server, se instala y (gracias
  // al skipWaiting) toma el control ya. Sin esto podia seguir activo un worker viejo.
  await reg.update().catch(() => {});
  await navigator.serviceWorker.ready;
  const { key } = await api.get<{ key: string }>('/api/push/vapid-public-key');
  const keyBytes = urlBase64ToUint8Array(key);
  let sub = await reg.pushManager.getSubscription();
  // Sub de una clave VAPID vieja → renovarla (si no, el server manda y el push service rechaza).
  if (sub && (force || !subMatchesKey(sub, keyBytes))) {
    const viejo = sub.endpoint;
    await sub.unsubscribe().catch(() => {});
    await api.post('/api/push/unsubscribe', { endpoint: viejo }).catch(() => {});
    sub = null;
  }
  if (!sub) {
    sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: keyBytes as unknown as BufferSource });
  }
  const json = sub.toJSON() as { endpoint?: string; keys?: { p256dh?: string; auth?: string } };
  await api.post('/api/push/subscribe', { endpoint: json.endpoint, keys: { p256dh: json.keys?.p256dh, auth: json.keys?.auth } });
  return true;
}

/**
 * Re-suscribe en SILENCIO si el usuario YA concedió permiso antes (no muestra prompt).
 * Se llama al confirmar login: reinstala el SW y re-registra la suscripción, que puede
 * haber caducado o quedado huérfana tras un deploy. Best-effort: nunca lanza.
 */
export async function ensurePushSubscribed(): Promise<void> {
  try {
    if (!pushSupported() || Notification.permission !== 'granted') return;
    const reg = await navigator.serviceWorker.register('/sw.js');
    // Fuerza el chequeo de version: si hay un sw.js nuevo en el server, se instala y (gracias
    // al skipWaiting) toma el control ya. Sin esto podia seguir activo un worker viejo.
    await reg.update().catch(() => {});
    await navigator.serviceWorker.ready;
    const { key } = await api.get<{ key: string }>('/api/push/vapid-public-key');
    const keyBytes = urlBase64ToUint8Array(key);
    let sub = await reg.pushManager.getSubscription();
    // Igual que en enablePush: sub de clave vieja → renovar en silencio.
    if (sub && !subMatchesKey(sub, keyBytes)) { await sub.unsubscribe().catch(() => {}); sub = null; }
    if (!sub) {
      sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: keyBytes as unknown as BufferSource });
    }
    const json = sub.toJSON() as { endpoint?: string; keys?: { p256dh?: string; auth?: string } };
    await api.post('/api/push/subscribe', { endpoint: json.endpoint, keys: { p256dh: json.keys?.p256dh, auth: json.keys?.auth } });
  } catch { /* best-effort */ }
}

export async function disablePush(): Promise<void> {
  if (!pushSupported()) return;
  const reg = await navigator.serviceWorker.getRegistration();
  const sub = await reg?.pushManager.getSubscription();
  if (sub) { await api.post('/api/push/unsubscribe', { endpoint: sub.endpoint }).catch(() => {}); await sub.unsubscribe().catch(() => {}); }
}
